import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { retryWhen, delay, repeatWhen, tap, take } from 'rxjs/operators';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { AuthenticationService } from './authentication.service';

const RETRY_DELAY_MILLISECONDS = 5000;
const MAX_RETRIES = 10;
const RECONNECT_DELAY_MILLISECONDS = 1500;

@Injectable({
    providedIn: 'root'
})
export class ChatStreamService {
    connection$: WebSocketSubject<any> | null;
    private chatUrl: string;
    constructor(
        private readonly authenticationService: AuthenticationService
    ) {
    }

    get bearerToken() {
        return this.authenticationService.bearerToken;
    }

    connect(sessionId: string, chatUrl: string): Observable<any> {
        this.chatUrl = chatUrl;
        const url = `${this.websocketBaseUrl}/${sessionId}?token=${this.bearerToken}`;
        this.connection$ ??= webSocket(url);
        return this.connection$.pipe(
            retryWhen(errors =>
                errors.pipe(
                    tap(err => console.error('Connection failed, retrying...', err)),
                    delay(RETRY_DELAY_MILLISECONDS), // Wait for 5 seconds before retrying
                    take(MAX_RETRIES) // Retry a maximum of 10 times
                )
            ),
            repeatWhen(completions =>
                completions.pipe(
                    delay(RECONNECT_DELAY_MILLISECONDS) // Wait for 1.5 seconds before reconnecting
                )
            )
        );
    }

    /**
     * Returns the stream URL for the chat service.
     */
    get websocketBaseUrl(): string {
        if (this.chatUrl.startsWith("https://")) {
            return this.chatUrl.replace("https", "wss") + "/chat-stream/ws";
        }
        return this.chatUrl.replace("http", "ws") + "/chat-stream/ws";
    }

    send(data: any) {
        this.connection$!.next(data);
    }

    closeConnection() {
        if (this.connection$) {
            this.connection$.unsubscribe();
            this.connection$.complete();
            this.connection$ = null;
        }
    }
    
    ngOnDestroy() {
        this.closeConnection();
    }
}
